import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {Users, Plus, Edit2, Trash2, Loader2, X, ShieldCheck, Lock, Leaf, Eye, EyeOff, KeyRound} from 'lucide-react'
import { formatarData } from '@/utils/protocolo'
import toast from 'react-hot-toast'
import { ADMIN_EMAIL, ADMIN_USERNAME, bootstrapAdminMaster } from '@/utils/bootstrapAdmin'

interface UsuarioSistema {
  _id: string
  userId: string
  userName: string
  email: string
  cargo: string
  userRole: string
  ativo: boolean
  senha?: string      // campo obrigatório no schema — gravado como senha inicial
  senhaAtual?: string // campo atualizado quando cidadão altera senha no Portal
  creator: string
  createdAt: string
  updatedAt: string
}

export default function UsuariosPage() {
  const [usuarios, setUsuarios] = useState<UsuarioSistema[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editItem, setEditItem] = useState<UsuarioSistema | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<UsuarioSistema | null>(null)
  const [saving, setSaving] = useState(false)
  // controle de visibilidade da senha por linha
  const [senhaVisivelId, setSenhaVisivelId] = useState<string | null>(null)

  const [form, setForm] = useState({
    userName: '',
    email: '',
    cargo: '',
    userRole: 'USER',
    ativo: true,
    senha: '',
  })

  const fetchUsuarios = () => {
    lumi.entities.usuarios_sistema.list({ sort: { createdAt: -1 }, limit: 200 }).then((r) => {
      setUsuarios((r.list || []) as UsuarioSistema[])
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  useEffect(() => { fetchUsuarios() }, [])

  const openNew = () => {
    setEditItem(null)
    setForm({ userName: '', email: '', cargo: '', userRole: 'USER', ativo: true, senha: '' })
    setShowModal(true)
  }

  const openEdit = (u: UsuarioSistema) => {
    setEditItem(u)
    setForm({ userName: u.userName, email: u.email, cargo: u.cargo, userRole: u.userRole, ativo: u.ativo, senha: '' })
    setShowModal(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.userName.trim()) { toast.error('Informe o nome do usuário.'); return }
    if (!form.email.trim()) { toast.error('Informe o e-mail.'); return }
    setSaving(true)
    try {
      const now = new Date().toISOString()
      if (editItem) {
        await lumi.entities.usuarios_sistema.update(editItem._id, {
          userName: form.userName,
          email: form.email,
          cargo: form.cargo,
          userRole: form.userRole,
          ativo: form.ativo,
          updatedAt: now,
        })
        toast.success('Usuário atualizado!')
      } else {
        const result = await lumi.auth.signUpWithPassword({
          email: form.email,
          password: form.senha || 'TempPass123!',
          userName: form.userName,
        }) as any
        await lumi.entities.usuarios_sistema.create({
          userId: result?.user?.userId || result?.userId || 'unknown',
          userName: form.userName,
          email: form.email,
          cargo: form.cargo,
          userRole: form.userRole,
          ativo: true,
          creator: 'sistema',
          createdAt: now,
          updatedAt: now,
        })
        toast.success('Usuário criado!')
      }
      setShowModal(false)
      fetchUsuarios()
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao salvar usuário.')
    } finally {
      setSaving(false)
    }
  }

  const isAdminMaster = (u: UsuarioSistema) =>
    u.userName === ADMIN_USERNAME || u.email === ADMIN_EMAIL

  const handleDelete = async (u: UsuarioSistema) => {
    if (isAdminMaster(u)) {
      toast.error('O usuário Admin Master não pode ser excluído do sistema.')
      setConfirmDelete(null)
      return
    }
    try {
      await lumi.entities.usuarios_sistema.delete(u._id)
      toast.success('Usuário removido!')
      setConfirmDelete(null)
      fetchUsuarios()
      bootstrapAdminMaster()
    } catch {
      toast.error('Erro ao remover usuário.')
    }
  }

  // Extrai o CPF do e-mail sintético de cidadão (ex: 05900103447@cidadao.geoa)
  const cpfDoCidadao = (u: UsuarioSistema) =>
    u.userRole === 'CIDADAO' ? u.email.replace('@cidadao.geoa', '') : ''

  /**
   * Senha visível para o admin (prioridade decrescente):
   * 1) senhaAtual  — atualizado quando o cidadão altera a senha no Portal
   * 2) senha        — gravado na criação (senha inicial = 6 primeiros dígitos do CPF)
   * 3) Deriva dos 6 primeiros dígitos do CPF extraído do e-mail sintético
   */
  const senhaDoCidadao = (u: UsuarioSistema) => {
    if (u.senhaAtual) return u.senhaAtual
    if (u.senha) return u.senha
    const cpf = cpfDoCidadao(u)
    return cpf ? cpf.slice(0, 6) : '—'
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Usuários</h1>
          <p className="text-sm text-gray-500 mt-0.5">{usuarios.length} usuário(s) cadastrado(s)</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-2xl transition"
          style={{ backgroundColor: '#0f3b50' }}
          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#0a2d3d'}
          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#0f3b50'}
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Novo Usuário</span>
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: '#0f3b50' }} />
            <span className="text-sm text-gray-500">Carregando...</span>
          </div>
        ) : usuarios.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-8 h-8 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Nenhum usuário cadastrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-100" style={{ backgroundColor: '#e8f1f5' }}>
                  <th className="text-left text-xs font-semibold px-4 py-3 uppercase tracking-wide" style={{ color: '#0f3b50' }}>Nome</th>
                  <th className="text-left text-xs font-semibold px-4 py-3 uppercase tracking-wide hidden md:table-cell" style={{ color: '#0f3b50' }}>Login / E-mail</th>
                  <th className="text-left text-xs font-semibold px-4 py-3 uppercase tracking-wide hidden lg:table-cell" style={{ color: '#0f3b50' }}>Cargo</th>
                  <th className="text-left text-xs font-semibold px-4 py-3 uppercase tracking-wide" style={{ color: '#0f3b50' }}>Perfil</th>
                  <th className="text-left text-xs font-semibold px-4 py-3 uppercase tracking-wide hidden sm:table-cell" style={{ color: '#0f3b50' }}>Status</th>
                  {/* Coluna de senha — só visível para linhas cidadão */}
                  <th className="text-left text-xs font-semibold px-4 py-3 uppercase tracking-wide hidden md:table-cell" style={{ color: '#0f3b50' }}>
                    <div className="flex items-center gap-1">
                      <KeyRound className="w-3 h-3" />
                      Senha Cidadão
                    </div>
                  </th>
                  <th className="text-right text-xs font-semibold px-4 py-3 uppercase tracking-wide" style={{ color: '#0f3b50' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u, i) => (
                  <tr key={u._id} className={`${i > 0 ? 'border-t border-gray-50' : ''} ${i % 2 === 1 ? 'bg-gray-50/40' : ''} hover:bg-blue-50/30 transition`}>
                    {/* Nome */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isAdminMaster(u) ? (
                          <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#0f3b50' }} title="Admin Master — protegido" />
                        ) : u.userRole === 'CIDADAO' ? (
                          <Leaf className="w-3.5 h-3.5 flex-shrink-0 text-green-600" title="Conta do Portal do Cidadão" />
                        ) : null}
                        <span className="text-sm font-medium text-gray-900">{u.userName}</span>
                        {isAdminMaster(u) && (
                          <span className="text-xs font-medium hidden lg:inline" style={{ color: '#0f3b50' }}>Master</span>
                        )}
                        {u.userRole === 'CIDADAO' && (
                          <span className="text-xs font-medium hidden lg:inline text-green-700">Portal</span>
                        )}
                      </div>
                    </td>

                    {/* Login */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      {u.userRole === 'CIDADAO' ? (
                        <div>
                          <span className="text-sm font-mono text-gray-700">{cpfDoCidadao(u)}</span>
                          <span className="ml-1 text-xs text-gray-400">(CPF)</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-600">{u.email}</span>
                      )}
                    </td>

                    {/* Cargo */}
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-sm text-gray-600">{u.cargo || '-'}</span>
                    </td>

                    {/* Perfil badge */}
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium
                        ${u.userRole === 'ADMIN'
                          ? 'bg-purple-50 text-purple-700 border-purple-200'
                          : u.userRole === 'CIDADAO'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                        {u.userRole === 'ADMIN' ? 'Admin' : u.userRole === 'CIDADAO' ? 'Cidadão' : 'Usuário'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium
                        ${u.ativo
                          ? 'bg-[#e8f1f5] text-[#0f3b50] border-[#b8d4e0]'
                          : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>

                    {/* Senha (só para cidadão) */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      {u.userRole === 'CIDADAO' ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-sm text-gray-800 tracking-widest">
                            {senhaVisivelId === u._id ? senhaDoCidadao(u) : '••••••'}
                          </span>
                          <button
                            onClick={() => setSenhaVisivelId(senhaVisivelId === u._id ? null : u._id)}
                            className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                            title={senhaVisivelId === u._id ? 'Ocultar senha' : 'Ver senha'}
                          >
                            {senhaVisivelId === u._id
                              ? <EyeOff className="w-3.5 h-3.5" />
                              : <Eye className="w-3.5 h-3.5" />
                            }
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>

                    {/* Ações */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {isAdminMaster(u) || u.userRole === 'CIDADAO' ? (
                          <span
                            title={isAdminMaster(u) ? 'Usuário Admin Master — protegido' : 'Conta do Portal do Cidadão — gerida automaticamente'}
                            className="p-1.5 text-gray-300 cursor-not-allowed"
                          >
                            <Lock className="w-3.5 h-3.5" />
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => openEdit(u)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setConfirmDelete(u)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal criar/editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold text-gray-900">
                {editItem ? 'Editar Usuário' : 'Novo Usuário'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.userName}
                  onChange={e => setForm(p => ({ ...p, userName: e.target.value }))}
                  placeholder="Nome do usuário"
                  className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  E-mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@prefeitura.gov.br"
                  className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
                  disabled={!!editItem}
                />
              </div>
              {!editItem && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Senha</label>
                  <input
                    type="password"
                    value={form.senha}
                    onChange={e => setForm(p => ({ ...p, senha: e.target.value }))}
                    placeholder="Senha inicial"
                    className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Cargo</label>
                <input
                  value={form.cargo}
                  onChange={e => setForm(p => ({ ...p, cargo: e.target.value }))}
                  placeholder="Ex: Secretário de Meio Ambiente"
                  className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Perfil</label>
                <select
                  value={form.userRole}
                  onChange={e => setForm(p => ({ ...p, userRole: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none bg-white"
                >
                  <option value="USER">Usuário</option>
                  <option value="ADMIN">Administrador</option>
                </select>
              </div>
              {editItem && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={e => setForm(p => ({ ...p, ativo: e.target.checked }))}
                    className="w-4 h-4" style={{ accentColor: '#0f3b50' } as React.CSSProperties}
                  />
                  <span className="text-sm text-gray-700">Usuário ativo</span>
                </label>
              )}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 text-white text-sm font-medium rounded-2xl transition flex items-center justify-center gap-2"
                  style={{ backgroundColor: saving ? '#9ca3af' : '#0f3b50' }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {editItem ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmar exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Remover Usuário</h2>
            <p className="text-sm text-gray-600 mb-5">
              Deseja remover o usuário <strong>"{confirmDelete.userName}"</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-2xl"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
